/*
 * Cart drawer — Shipping protection toggle wired to the Route widget.
 *
 * Route (Protection Tracking app) injects, in PRODUCTION ONLY, a light-DOM
 * widget at button[name=checkout] containing two CTAs:
 *   - WITH protection:    button[data-route-cloned-button]  (a clone of our
 *     #CartDrawer-Checkout; premium text lives in .route-premium-text-stacked,
 *     e.g. "+$8.55 Package Protection")
 *   - WITHOUT protection: button.route-continue-without-coverage-link
 *
 * Our toggle decides which CTA is visible (via the .protection-on/.protection-off
 * class on #CartDrawer — see component-cart-drawer.css) and mirrors the premium
 * value into the toggle row. The actual coverage choice happens when the user
 * clicks the corresponding Route button (Route owns the checkout flow).
 *
 * The drawer replaces .drawer__inner on every cart update and Route re-injects
 * its widget, so we (a) delegate the click from #CartDrawer (which persists) and
 * (b) re-apply state + re-read the premium on every mutation (MutationObserver).
 * State is kept in localStorage so the choice survives re-renders / reopen.
 */
(function () {
  var STORE_KEY = 'fp_shipping_protection'; // 'on' | 'off'

  function getState() {
    try {
      return localStorage.getItem(STORE_KEY) === 'off' ? 'off' : 'on';
    } catch (e) {
      return 'on';
    }
  }

  function setState(v) {
    try {
      localStorage.setItem(STORE_KEY, v);
    } catch (e) {}
  }

  function drawer() {
    return document.getElementById('CartDrawer');
  }

  function applyState(state) {
    var d = drawer();
    if (!d) return;
    d.classList.toggle('protection-on', state === 'on');
    d.classList.toggle('protection-off', state === 'off');
    var sw = d.querySelector('[data-protection-switch]');
    if (sw) {
      sw.classList.toggle('is-on', state === 'on');
      sw.setAttribute('aria-checked', state === 'on' ? 'true' : 'false');
    }
  }

  // Parse a formatted money string ("$8.55", "+$8.55", "1.234,56") into cents.
  function toCents(str) {
    var n = (str || '').replace(/[^\d.,]/g, '');
    if (!n) return 0;
    var dec = Math.max(n.lastIndexOf('.'), n.lastIndexOf(','));
    if (dec === -1) return (parseInt(n, 10) || 0) * 100;
    var intPart = n.slice(0, dec).replace(/[.,]/g, '');
    var fracPart = (n.slice(dec + 1).replace(/[.,]/g, '') + '00').slice(0, 2);
    return (parseInt(intPart, 10) || 0) * 100 + (parseInt(fracPart, 10) || 0);
  }

  // Shopify's standard money formatter (handles the common money_format tokens).
  function formatMoney(cents, format) {
    var fmt = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var value;
    switch ((fmt.match(placeholder) || [])[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  // Mirror Route's premium into the toggle row; returns the premium in cents.
  function syncPremium() {
    var d = drawer();
    if (!d) return 0;
    var src = d.querySelector('.route-premium-text-stacked') || d.querySelector('.route-premium');
    if (!src) return 0;
    var priceEl = d.querySelector('[data-protection-premium]');
    var match = src.textContent.match(/[£$€]\s?[\d.,]+/);
    if (match && priceEl) priceEl.textContent = match[0].replace(/\s/g, '');
    return toCents(src.textContent);
  }

  // Displayed total = base (real cart) + premium when ON. The REAL Shopify cart
  // total is unchanged — Route charges the premium at its own checkout.
  function updateTotal(state, premiumCents) {
    var d = drawer();
    if (!d) return;
    var totalEl = d.querySelector('[data-cart-total]');
    var amountEl = totalEl && totalEl.querySelector('[data-total-amount]');
    if (!amountEl) return;
    var base = parseInt(totalEl.getAttribute('data-base-cents'), 10);
    if (isNaN(base)) return;
    var fmt = totalEl.getAttribute('data-money-format') || '${{amount}}';
    var total = base + (state === 'on' ? (premiumCents || 0) : 0);
    amountEl.textContent = formatMoney(total, fmt);
  }

  // True if any item in the cart needs shipping. A cart with only digital
  // products (programs, e-books) returns false — protection is irrelevant
  // there. Reads cart.requires_shipping rendered into the drawer, which
  // re-renders on every cart change.
  function cartNeedsShipping() {
    var d = drawer();
    var el = d && d.querySelector('[data-shipping-protection]');
    if (!el || !el.hasAttribute('data-cart-requires-shipping')) return true;
    return el.getAttribute('data-cart-requires-shipping') !== 'false';
  }

  function refresh() {
    var d = drawer();
    var protectionEl = d && d.querySelector('[data-shipping-protection]');
    // Nothing to ship (e.g. a digital-only cart): hide the toggle and keep
    // protection off, without overwriting the saved preference for later carts
    // that do contain shippable products.
    if (!cartNeedsShipping()) {
      if (protectionEl) protectionEl.style.display = 'none';
      applyState('off');
      updateTotal('off', 0);
      if (d) d.classList.toggle('has-route-protection', routeCtaVisible());
      return;
    }
    if (protectionEl) protectionEl.style.display = '';
    var state = getState();
    applyState(state);
    var premiumCents = syncPremium();
    updateTotal(state, premiumCents);
    // Hide our native checkout ONLY when Route actually renders a visible CTA
    // (the cloned "with protection" button or the "without coverage" button).
    // If Route's widget is present but renders no usable button — e.g. a
    // digital/program cart with nothing to ship, or Route's mobile behavior —
    // our checkout stays visible as the fallback.
    if (d) d.classList.toggle('has-route-protection', routeCtaVisible());
  }

  // True only if Route has rendered a checkout CTA that is actually visible.
  function routeCtaVisible() {
    var d = drawer();
    if (!d) return false;
    var btns = d.querySelectorAll('[data-route-cloned-button], .route-continue-without-coverage-link');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.offsetWidth > 0 || b.offsetHeight > 0 || b.getClientRects().length) return true;
    }
    return false;
  }

  function toggle() {
    if (!cartNeedsShipping()) return;
    var next = getState() === 'on' ? 'off' : 'on';
    setState(next);
    refresh();
  }

  function onClick(e) {
    var sw = e.target.closest && e.target.closest('[data-protection-switch]');
    var d = drawer();
    if (!sw || !d || !d.contains(sw)) return;
    toggle();
  }

  function onKey(e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var sw = e.target.closest && e.target.closest('[data-protection-switch]');
    if (!sw) return;
    e.preventDefault();
    toggle();
  }

  function start() {
    var d = drawer();
    if (!d) return;
    // Delegated listeners live on #CartDrawer, which persists across re-renders.
    d.addEventListener('click', onClick);
    d.addEventListener('keydown', onKey);
    refresh();

    var debounceId;
    new MutationObserver(function () {
      clearTimeout(debounceId);
      debounceId = setTimeout(refresh, 80);
    }).observe(d, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
